import { darken, lighten } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useMemo } from 'react'
import {
    NAV_DISCREET_DARKEN_PERCENTAGE,
    NAV_DISCREET_LIGHTEN_PERCENTAGE,
    NAV_EVIDENT_DARKEN_PERCENTAGE,
    NAV_EVIDENT_LIGHTEN_PERCENTAGE
} from '@/theme/colors'

export const useNavCssVars = (color) => {
    const theme = useTheme()

    return useMemo(() => {
        const isDark = theme.palette.mode === 'dark'
        const p = theme.palette
        const isEvident = color === 'evident'
        const isBlendIn = color === 'blend-in'

        // Background varies by both color variant and theme mode
        let navBg = p.background.default
        if (color === 'discreet') {
            navBg = isDark
                ? lighten(p.background.default, NAV_DISCREET_LIGHTEN_PERCENTAGE)
                : darken(p.background.default, NAV_DISCREET_DARKEN_PERCENTAGE)
        } else if (isEvident) {
            navBg = isDark
                ? lighten(p.background.default, NAV_EVIDENT_LIGHTEN_PERCENTAGE)
                : darken(p.background.default, NAV_EVIDENT_DARKEN_PERCENTAGE)
        }

        return {
            '--nav-bg': navBg,
            '--nav-color': isEvident
                ? p.common.white
                : isDark
                  ? p.neutral[100]
                  : p.text.primary,
            '--nav-border-color': isEvident
                ? 'transparent'
                : isDark
                  ? p.neutral[700]
                  : isBlendIn
                    ? p.neutral[100]
                    : p.divider,
            '--nav-logo-border': isDark
                ? p.neutral[700]
                : isBlendIn
                  ? p.neutral[100]
                  : isEvident
                    ? p.neutral[700]
                    : p.neutral[200],
            '--nav-section-title-color': isDark
                ? p.neutral[600]
                : p.neutral[700],
            '--nav-item-color':
                isDark && isEvident
                    ? p.neutral[600]
                    : isDark
                      ? p.neutral[500]
                      : p.neutral[600],
            '--nav-item-hover-bg': isDark
                ? isEvident
                    ? alpha(p.common.white, 0.08)
                    : alpha(p.common.white, 0.05)
                : alpha(p.neutral[900], 0.035),
            '--nav-item-active-bg': isDark
                ? isEvident
                    ? alpha(p.common.white, 0.12)
                    : alpha(p.common.white, 0.08)
                : alpha(p.neutral[900], 0.05),
            '--nav-item-border-color': isDark
                ? isEvident
                    ? alpha(p.common.white, 0.08)
                    : alpha(p.common.white, 0.05)
                : alpha(p.neutral[900], 0.08),
            '--nav-item-hover-border-color': isDark
                ? isEvident
                    ? alpha(p.common.white, 0.16)
                    : alpha(p.common.white, 0.1)
                : alpha(p.neutral[900], 0.14),
            '--nav-item-active-border-color': isDark
                ? isEvident
                    ? alpha(p.common.white, 0.24)
                    : alpha(p.common.white, 0.16)
                : alpha(p.neutral[900], 0.18),
            '--nav-item-active-color':
                isDark && isEvident ? p.common.white : p.text.primary,
            '--nav-item-disabled-color': isDark
                ? isEvident
                    ? p.neutral[500]
                    : p.neutral[600]
                : color === 'discreet'
                  ? p.neutral[300]
                  : p.neutral[400],
            '--nav-item-icon-color': isDark
                ? isEvident
                    ? p.neutral[400]
                    : p.neutral[500]
                : p.neutral[600],
            '--nav-item-icon-active-color': isDark
                ? isEvident
                    ? p.common.white
                    : p.neutral[100]
                : p.neutral[700],
            '--nav-item-icon-disabled-color': isDark
                ? isEvident
                    ? p.neutral[500]
                    : p.neutral[700]
                : isEvident
                  ? p.neutral[500]
                  : p.neutral[400],
            '--nav-item-chevron-color': isDark
                ? isEvident
                    ? p.neutral[600]
                    : p.neutral[700]
                : isEvident
                  ? p.neutral[600]
                  : p.neutral[400],
            '--nav-scrollbar-color': isDark
                ? p.neutral[400]
                : isEvident
                  ? p.neutral[400]
                  : p.neutral[900]
        }
    }, [theme, color])
}
